import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { APPEAL_STATUS, fmtDate, fmtDateTime } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const AppealDetailDialog = ({ appeal, isAdmin, onClose, onChange }) => {
  const [assessors, setAssessors] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [resolution, setResolution] = useState(appeal.resolution || "");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/assessors").then((r) => setAssessors(r.data.filter((a) => a.id !== appeal.original_assessor_id)));
    api.get("/assessments", { params: { rider_id: appeal.rider_id } }).then((r) => setAssessments(r.data.filter((a) => a.id !== appeal.assessment_id && a.assessor_id !== appeal.original_assessor_id)));
  }, [isAdmin, appeal.rider_id, appeal.assessment_id, appeal.original_assessor_id]);

  const patch = async (body, msg = "Updated") => {
    try {
      const { data } = await api.patch(`/appeals/${appeal.id}`, body);
      toast.success(msg);
      onChange(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.post(`/appeals/${appeal.id}/notes`, { text: note });
    setNote("");
    onChange(data);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="appeal-detail-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{appeal.kind === "appeal" ? "Appeal" : "Complaint"} · {appeal.rider_name}</DialogTitle></DialogHeader>
        <div className="space-y-1 text-sm">
          <p className="text-muted-foreground">Received {fmtDate(appeal.received_date)} · Assessment {fmtDate(appeal.assessment_date)} by {appeal.original_assessor_name || "—"}</p>
          <p className="whitespace-pre-wrap rounded-xl bg-secondary/50 p-3">{appeal.details}</p>
        </div>
        {isAdmin ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={appeal.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger data-testid="appeal-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(APPEAL_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Neutral assessor for re-test</Label>
              <Select value={appeal.neutral_assessor_id || undefined} onValueChange={(v) => patch({ neutral_assessor_id: v }, "Neutral assessor assigned")}>
                <SelectTrigger data-testid="appeal-neutral-select"><SelectValue placeholder="Select assessor" /></SelectTrigger>
                <SelectContent>{assessors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Link re-test assessment</Label>
              <Select value={appeal.retest_assessment_id || undefined} onValueChange={(v) => patch({ retest_assessment_id: v }, "Re-test linked")}>
                <SelectTrigger data-testid="appeal-retest-select"><SelectValue placeholder={assessments.length ? "Select re-test" : "No eligible re-test recorded yet"} /></SelectTrigger>
                <SelectContent>{assessments.map((a) => <SelectItem key={a.id} value={a.id}>{fmtDate(a.date)} · {a.assessor_name} · {a.outcome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Resolution</Label>
              <Textarea rows={2} value={resolution} onChange={(e) => setResolution(e.target.value)} data-testid="appeal-resolution-input" />
              <button onClick={() => patch({ resolution }, "Resolution saved")} data-testid="appeal-resolution-save" className="rounded-full border px-4 py-1.5 text-xs font-semibold hover:bg-secondary">Save resolution</button>
            </div>
          </div>
        ) : (
          <div className="space-y-1 text-sm">
            <p>Status: <b>{APPEAL_STATUS[appeal.status]}</b></p>
            <p>Neutral assessor: {appeal.neutral_assessor_name || "—"}</p>
            <p>Resolution: {appeal.resolution || "—"}</p>
          </div>
        )}
        <div className="space-y-2">
          <h4 className="font-bold">Record log</h4>
          <ol className="space-y-2 border-l pl-4" data-testid="appeal-log">
            {appeal.log.map((l, i) => <li key={i} className="text-sm"><span className="text-xs text-muted-foreground">{fmtDateTime(l.at)} · {l.by}</span><br />{l.text}</li>)}
          </ol>
          {isAdmin && (
            <div className="flex gap-2">
              <Input placeholder="Add a note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="appeal-note-input" />
              <button onClick={addNote} data-testid="appeal-note-add" className="rounded-full bg-primary px-4 text-sm font-bold text-primary-foreground">Add</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
